// The recording timeline: programme coverage with its data flows laid on
// the same wallclock axis (chapters, highlight marks, speaker turns, clips,
// thumbnails), a playhead, and an in/out selection for manual clipping.
//
// All times are epoch seconds. The component is controlled: the page owns
// the playhead and the selection.
import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '@/lib/utils';
import type { Chapter, Clip, DiarizationTurn, HighlightMarkData } from '@/types/processing';
import { formatOffset } from './shared';

export interface TimelineMark {
  start: number;
  end: number;
  data: HighlightMarkData;
}

export interface Selection {
  start: number;
  end: number;
}

export interface RecordingTimelineProps {
  start: number;
  end: number;
  playhead: number | null;
  selection: Selection | null;
  chapters: Chapter[];
  marks: TimelineMark[];
  turns: DiarizationTurn[];
  clips: Clip[];
  thumbnailTimes: number[];
  onSeek: (t: number) => void;
  onSelect: (sel: Selection | null) => void;
}

const SPEAKER_BARS = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-purple-500', 'bg-rose-500', 'bg-cyan-500'];

export function speakerColor(index: number): string {
  return SPEAKER_BARS[index % SPEAKER_BARS.length];
}

/** Minimum drag, in seconds, before a pointer gesture is a selection rather than a seek. */
const MIN_SELECTION_S = 1;

function Lane({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-stretch gap-2">
      <div className="w-20 shrink-0 text-[10px] uppercase tracking-wider text-jeket-silver-muted pt-1">{label}</div>
      <div className="relative flex-1 h-6 bg-jeket-black-deep rounded">{children}</div>
    </div>
  );
}

export default function RecordingTimeline({
  start,
  end,
  playhead,
  selection,
  chapters,
  marks,
  turns,
  clips,
  thumbnailTimes,
  onSeek,
  onSelect,
}: RecordingTimelineProps) {
  const span = Math.max(end - start, 1);
  const trackRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ from: number; to: number } | null>(null);

  const pct = useCallback((t: number) => ((Math.min(Math.max(t, start), end) - start) / span) * 100, [start, end, span]);

  const timeAt = useCallback(
    (clientX: number) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return start;
      const frac = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
      return start + frac * span;
    },
    [start, span],
  );

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const t = timeAt(e.clientX);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setDrag({ from: t, to: t });
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (drag) setDrag({ ...drag, to: timeAt(e.clientX) });
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    const to = timeAt(e.clientX);
    const a = Math.min(drag.from, to);
    const b = Math.max(drag.from, to);
    setDrag(null);
    if (b - a >= MIN_SELECTION_S) onSelect({ start: a, end: b });
    else onSeek(drag.from);
  };

  const speakerIndex = new Map<string, number>();
  for (const t of turns) if (!speakerIndex.has(t.speaker)) speakerIndex.set(t.speaker, speakerIndex.size);

  const shownSel = drag
    ? { start: Math.min(drag.from, drag.to), end: Math.max(drag.from, drag.to) }
    : selection;

  // Tick every ~1/6 of the span, rounded to a whole minute when long enough.
  const tickStep = span > 600 ? Math.ceil(span / 6 / 60) * 60 : Math.ceil(span / 6);
  const ticks: number[] = [];
  for (let t = 0; t <= span * 0.95; t += tickStep) ticks.push(t);

  return (
    <div className="space-y-1.5 select-none" data-testid="recording-timeline">
      {/* Programme lane: the interactive track */}
      <div className="flex items-stretch gap-2">
        <div className="w-20 shrink-0 text-[10px] uppercase tracking-wider text-jeket-gold pt-2">Programme</div>
        <div
          ref={trackRef}
          role="slider"
          aria-label="Recording timeline"
          aria-valuemin={0}
          aria-valuemax={Math.round(span)}
          aria-valuenow={playhead != null ? Math.round(playhead - start) : 0}
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onKeyDown={(e) => {
            const base = playhead ?? start;
            if (e.key === 'ArrowRight') onSeek(Math.min(base + 5, end));
            if (e.key === 'ArrowLeft') onSeek(Math.max(base - 5, start));
          }}
          className="relative flex-1 h-10 bg-jeket-gold/15 border border-jeket-gold/30 rounded cursor-crosshair touch-none focus:outline-none focus:ring-1 focus:ring-jeket-gold/60"
        >
          {thumbnailTimes.map((t, i) => (
            <div
              key={`th-${i}`}
              className="absolute top-0 w-0.5 h-2 bg-orange-400"
              style={{ left: `${pct(t)}%` }}
              title={`Thumbnail at ${formatOffset(t - start)}`}
            />
          ))}
          {shownSel && (
            <div
              data-testid="timeline-selection"
              className="absolute inset-y-0 bg-jeket-info/30 border-x-2 border-jeket-info"
              style={{ left: `${pct(shownSel.start)}%`, width: `${pct(shownSel.end) - pct(shownSel.start)}%` }}
            />
          )}
          {playhead != null && (
            <div
              className="absolute -top-1 -bottom-1 w-0.5 bg-white shadow"
              style={{ left: `${pct(playhead)}%` }}
            />
          )}
        </div>
      </div>

      {chapters.length > 0 && (
        <Lane label="Chapters">
          {chapters.map((c, i) => (
            <button
              key={`ch-${i}`}
              type="button"
              onClick={() => onSeek(c.start)}
              title={`${c.title} (${formatOffset(c.start - start)})`}
              className={cn(
                'absolute inset-y-0.5 rounded-sm px-1 text-[10px] text-left truncate border-l border-jeket-black-deep',
                i % 2 ? 'bg-green-500/30 text-green-200' : 'bg-green-500/20 text-green-200',
              )}
              style={{ left: `${pct(c.start)}%`, width: `${Math.max(pct(c.end) - pct(c.start), 0.5)}%` }}
            >
              {c.title}
            </button>
          ))}
        </Lane>
      )}

      {marks.length > 0 && (
        <Lane label="Highlights">
          {marks.map((m, i) => (
            <button
              key={`mk-${i}`}
              type="button"
              onClick={() => onSelect({ start: m.start, end: m.end })}
              title={`${m.data.label ?? m.data.reason} · ${Math.round(m.data.score * 100)}%`}
              className="absolute inset-y-0.5 rounded-sm bg-yellow-400/70 hover:bg-yellow-300"
              style={{ left: `${pct(m.start)}%`, width: `${Math.max(pct(m.end) - pct(m.start), 0.4)}%` }}
            />
          ))}
        </Lane>
      )}

      {turns.length > 0 && (
        <Lane label="Speakers">
          {turns.map((t, i) => (
            <div
              key={`tn-${i}`}
              className={cn('absolute inset-y-1 opacity-70', speakerColor(speakerIndex.get(t.speaker) ?? 0))}
              style={{ left: `${pct(t.start)}%`, width: `${Math.max(pct(t.end) - pct(t.start), 0.1)}%` }}
              title={`${t.speaker}: ${formatOffset(t.start - start)}–${formatOffset(t.end - start)}`}
            />
          ))}
        </Lane>
      )}

      {clips.length > 0 && (
        <Lane label="Clips">
          {clips.map((c) => (
            <button
              key={c.jobId}
              type="button"
              onClick={() => onSeek(c.start)}
              title={`${c.title ?? 'Clip'} · ${c.status}`}
              className={cn(
                'absolute inset-y-0.5 rounded-sm',
                c.approved ? 'bg-jeket-healthy/70' : c.status === 'failed' ? 'bg-jeket-error/60' : 'bg-pink-400/60',
              )}
              style={{ left: `${pct(c.start)}%`, width: `${Math.max(pct(c.end) - pct(c.start), 0.4)}%` }}
            />
          ))}
        </Lane>
      )}

      {/* Axis */}
      <div className="flex gap-2">
        <div className="w-20 shrink-0" />
        <div className="relative flex-1 h-4 text-[10px] text-jeket-silver-muted tabular-nums">
          {ticks.map((t) => (
            <span key={t} className="absolute -translate-x-1/2" style={{ left: `${(t / span) * 100}%` }}>
              {formatOffset(t)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
